import { useEffect, useState } from 'react';
import { EnterpriseWorkspaceNav } from '../components/enterprise/EnterpriseWorkspaceNav';
import { Card } from '../components/ui/card';
import { useEnterpriseWorkspaceRole } from '../hooks/use-enterprise-workspace-role';
import { useEnterpriseWorkspaceCheckIns } from '../hooks/use-enterprise-workspace-checkins';
import { useAuth } from '../lib/auth-context';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import { fetchEnterpriseWorkspaceInsights, type EnterpriseWorkspaceInsights } from '../lib/enterprise-api';
import { useEnterpriseWorkspaceInterventions } from '../hooks/use-enterprise-workspace-interventions';
import { useEnterpriseWorkspaceAnalytics } from '../hooks/use-enterprise-workspace-analytics';

export default function EnterpriseWorkspaceInsights() {
  const { role } = useEnterpriseWorkspaceRole();
  const { rows, loading, error } = useEnterpriseWorkspaceCheckIns();
  const { accessToken } = useAuth();
  const { activeOrgId, hasActiveOrgAccess } = useEnterpriseOrg();
  const [insights, setInsights] = useState<EnterpriseWorkspaceInsights | null>(null);
  const [domainCounts, setDomainCounts] = useState<Record<string, number> | null>(null);
  const [insightsScope, setInsightsScope] = useState<'organization' | 'self' | null>(null);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const interventions = useEnterpriseWorkspaceInterventions(30);
  const analytics = useEnterpriseWorkspaceAnalytics();

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !activeOrgId || !hasActiveOrgAccess) {
      setInsights(null);
      setDomainCounts(null);
      setInsightsScope(null);
      setInsightsError(null);
      return;
    }
    setInsightsError(null);
    void fetchEnterpriseWorkspaceInsights(accessToken, activeOrgId, 90).then((res) => {
      if (cancelled) return;
      if (!res.success || !res.summary) {
        setInsights(null);
        setDomainCounts(null);
        setInsightsScope(null);
        setInsightsError(res.error || 'Workspace insights endpoint unavailable');
        return;
      }
      setInsights(res.summary);
      setDomainCounts(res.domainCounts || {});
      setInsightsScope(res.scope || null);
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, activeOrgId, hasActiveOrgAccess]);

  const byDomainLocal = rows.reduce<Record<string, number>>((acc, row) => {
    acc[row.relationshipDomain] = (acc[row.relationshipDomain] || 0) + 1;
    return acc;
  }, {});

  const avg = rows.length ? Math.round(rows.reduce((sum, r) => sum + r.rtiScore, 0) / rows.length) : 0;
  const high = rows.filter((r) => r.rtiScore >= 61).length;
  const highPct = rows.length ? Math.round((high / rows.length) * 100) : 0;
  const leadershipAvg = rows.filter((r) => r.relationshipDomain === 'leadership');
  const leadershipScore = leadershipAvg.length
    ? Math.round(leadershipAvg.reduce((sum, row) => sum + row.rtiScore, 0) / leadershipAvg.length)
    : null;

  const recommendation =
    role === 'executive'
      ? 'Prioritize trend movement and intervention throughput in monthly operating reviews.'
      : role === 'hr'
        ? 'Focus on high-tension cohorts and escalation response timing this week.'
        : role === 'manager'
          ? 'Use team-level check-ins to spot drift early and trigger recovery conversations.'
          : 'Review workflow quality and handoff latency for flagged cohorts.';

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#020617_0%,#0b1220_100%)]">
      <EnterpriseWorkspaceNav />
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
        <h1 className="text-2xl font-bold text-slate-100">Enterprise insights</h1>
        {loading ? <p className="text-xs text-slate-400">Loading server-backed insights…</p> : null}
        {error ? <p className="text-xs text-amber-300">Server data warning: {error}</p> : null}
        {insightsScope ? <p className="text-xs text-blue-300">Workspace scope: {insightsScope}</p> : null}
        {insightsError ? <p className="text-xs text-amber-300">Insights endpoint warning: {insightsError}</p> : null}
        {analytics.analytics?.scope ? <p className="text-xs text-blue-300">Analytics scope: {analytics.analytics.scope}</p> : null}
        {analytics.error ? <p className="text-xs text-amber-300">Analytics warning: {analytics.error}</p> : null}
        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-400">Role-aware recommendation</p>
          <p className="mt-2 text-sm text-slate-200">{recommendation}</p>
        </Card>
        <div className="grid gap-3 md:grid-cols-3">
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">Average RTI</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">
              {insights ? insights.avgRti : rows.length ? avg : '—'}
            </p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">High tension count</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">{insights ? insights.highCount : high}</p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">High tension rate</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">
              {insights ? `${insights.highRatePct}%` : rows.length ? `${highPct}%` : '—'}
            </p>
          </Card>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">7d vs 30d trend delta</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">
              {analytics.analytics ? `${analytics.analytics.windows.trendDelta7v30 > 0 ? '+' : ''}${analytics.analytics.windows.trendDelta7v30}` : '—'}
            </p>
            <p className="mt-1 text-xs text-slate-400">Avg RTI movement (positive means rising tension).</p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">30d intervention load</p>
            <p className="mt-2 text-sm text-slate-200">
              {analytics.analytics
                ? `Critical ${analytics.analytics.interventionLoad30.critical} · High ${analytics.analytics.interventionLoad30.high} · Elevated ${analytics.analytics.interventionLoad30.elevated}`
                : '—'}
            </p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">Activity density</p>
            <p className="mt-2 text-sm text-slate-200">
              {analytics.analytics
                ? `7d ${analytics.analytics.windows.count7} · 30d ${analytics.analytics.windows.count30} · 90d ${analytics.analytics.windows.count90}`
                : '—'}
            </p>
          </Card>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">Response SLA target</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">
              {analytics.analytics ? `${analytics.analytics.responseSlaHours}h` : '—'}
            </p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">30d SLA breaches</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">
              {analytics.analytics ? analytics.analytics.slaBreaches30 : '—'}
            </p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">Queue aging (30d)</p>
            <p className="mt-2 text-sm text-slate-200">
              {analytics.analytics
                ? `<24h ${analytics.analytics.queueAging30.under24h} · 24-72h ${analytics.analytics.queueAging30.between24hTo72h} · 72h+ ${analytics.analytics.queueAging30.over72h}`
                : '—'}
            </p>
          </Card>
        </div>

        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-sm font-semibold text-slate-100">Domain distribution</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-300">
            {Object.keys(domainCounts || byDomainLocal).length === 0 ? (
              <li>No check-ins recorded yet.</li>
            ) : (
              Object.entries(domainCounts || byDomainLocal).map(([domain, count]) => (
                <li key={domain}>
                  {domain}: {count}
                </li>
              ))
            )}
          </ul>
        </Card>
        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-sm font-semibold text-slate-100">Leadership dynamics signal</p>
          <p className="mt-2 text-sm text-slate-300">
            {(insights?.leadershipAvg ?? leadershipScore) == null
              ? 'No leadership-domain check-ins logged yet.'
              : `Average leadership-domain RTI: ${insights?.leadershipAvg ?? leadershipScore}.`}
          </p>
        </Card>
        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-sm font-semibold text-slate-100">Trigger intelligence (top 30d)</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-300">
            {!analytics.analytics || analytics.analytics.topTriggers30.length === 0 ? (
              <li>No trigger signal density yet.</li>
            ) : (
              analytics.analytics.topTriggers30.map((item) => (
                <li key={item.trigger}>
                  {item.trigger}: {item.count}
                </li>
              ))
            )}
          </ul>
        </Card>
        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-sm font-semibold text-slate-100">Role playbook</p>
          <p className="mt-2 text-sm text-slate-200">
            {role === 'executive'
              ? 'Run a weekly operating review on trend delta, critical queue volume, and intervention closure rate.'
              : role === 'hr'
                ? 'Prioritize critical and high queue items, assign response owners, and confirm escalation SLAs.'
                : role === 'manager'
                  ? 'Focus on elevated/high team hotspots and run quick recovery loops within 24 hours.'
                  : 'Track workflow handoff delays, document causes, and escalate recurring trigger patterns.'}
          </p>
        </Card>
        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-sm font-semibold text-slate-100">Intervention priority queue</p>
          {interventions.loading ? <p className="mt-2 text-sm text-slate-300">Loading intervention queue…</p> : null}
          {interventions.error ? <p className="mt-2 text-sm text-amber-300">{interventions.error}</p> : null}
          {!interventions.loading && !interventions.error ? (
            <>
              {interventions.roleAction ? <p className="mt-2 text-sm text-slate-200">{interventions.roleAction}</p> : null}
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                {interventions.queue.length === 0 ? (
                  <li>No high-tension interventions in the active window.</li>
                ) : (
                  interventions.queue.slice(0, 5).map((item) => (
                    <li key={item.id}>
                      <span className="font-semibold text-slate-100">{item.severity.toUpperCase()}</span> · RTI {item.rtiScore} ·{' '}
                      {item.relationshipDomain}
                      {item.entityLabel ? ` · ${item.entityLabel}` : ''} — {item.recommendation}
                    </li>
                  ))
                )}
              </ul>
            </>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

